import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Portal,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { useNavigate } from 'react-router-dom';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import styles from './BalanceControlDialog.module.scss';

const BalanceControlDialog = ({ open, onClose, balanceControlData, client }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const direction = i18n.dir() === 'rtl' ? 'rtl' : 'ltr';
    const rows = Array.isArray(balanceControlData) ? balanceControlData : [];

    const getMonthStartAndEnd = (monthYear) => {
        const [month, year] = monthYear.split('/').map(Number);
        const fullYear = year < 100 ? 2000 + year : year;
        const start = new Date(fullYear, month - 1, 3);
        const end = new Date(fullYear, month, 0);
        return { start, end };
    };

    const viewDocumentDetails = (type, start, end) => {
        navigate('/Document', { state: { type, client, start, end } });
    };

    const viewJournalEntryDetails = (type, start, end) => {
        navigate('/JournalEntry', { state: { type, client, start, end } });
    };

    const viewPaymentAcceptanceDetails = (type, start, end) => {
        navigate('/PaymentAcceptance', { state: { type, client, start, end } });
    };

    if (!open) return null;

    return (
        <Portal>
            <div
                className={`${styles.overlay} ${direction === 'rtl' ? styles.rtl : styles.ltr}`}
                onClick={onClose}
            >
                <div className={styles.modal} dir={direction} onClick={(e) => e.stopPropagation()}>
                    <div className={styles.body}>
                        <div className={styles.header}>
                            <Tooltip title={t('Close')}>
                                <IconButton onClick={onClose} className={styles.closeIcon}>
                                    <CancelIcon className={styles.closeIcon} />
                                </IconButton>
                            </Tooltip>
                            <div>{t('BalanceControl')}</div>
                            <div />
                        </div>

                        <div className={styles.tableWrap}>
                            <TableContainer component={Paper}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('MonthYear')}</TableCell>
                                            <TableCell>{t('Incomes')}</TableCell>
                                            <TableCell>{t('Expenses')}</TableCell>
                                            <TableCell>{t('JournalEntries')}</TableCell>
                                            <TableCell>{t('PaymentAcceptance')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5}>
                                                    <div className={styles.emptyState}>{t('NoData')}</div>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            rows.map((row, index) => {
                                                const { start, end } = getMonthStartAndEnd(row.monthYear);
                                                return (
                                                    <TableRow key={index} hover>
                                                        <TableCell>{row.monthYear}</TableCell>
                                                        <TableCell
                                                            className={styles.clickable}
                                                            onClick={() => viewDocumentDetails('Incomes', start, end)}
                                                        >
                                                            {beautifyNumber(row.income)}
                                                        </TableCell>
                                                        <TableCell
                                                            className={styles.clickable}
                                                            onClick={() => viewDocumentDetails('Expenses', start, end)}
                                                        >
                                                            {beautifyNumber(row.expense)}
                                                        </TableCell>
                                                        <TableCell
                                                            className={styles.clickable}
                                                            onClick={() => viewJournalEntryDetails('JournalEntries', start, end)}
                                                        >
                                                            {beautifyNumber(row.journalEntries)}
                                                        </TableCell>
                                                        <TableCell
                                                            className={styles.clickable}
                                                            onClick={() => viewPaymentAcceptanceDetails('PaymentAcceptances', start, end)}
                                                        >
                                                            {beautifyNumber(row.paymentAcceptance)}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                        {rows.length > 0 && (
                                            <TableRow className={styles.totalRow}>
                                                <TableCell>{t('Total')}</TableCell>
                                                <TableCell>
                                                    {beautifyNumber(rows.reduce((sum, row) => sum + row.income, 0))}
                                                </TableCell>
                                                <TableCell>
                                                    {beautifyNumber(rows.reduce((sum, row) => sum + row.expense, 0))}
                                                </TableCell>
                                                <TableCell>
                                                    {beautifyNumber(rows.reduce((sum, row) => sum + row.journalEntries, 0))}
                                                </TableCell>
                                                <TableCell>
                                                    {beautifyNumber(rows.reduce((sum, row) => sum + row.paymentAcceptance, 0))}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>

                        <div className={styles.footer}>
                            <button type="button" className={styles.secondaryBtn} onClick={onClose}>
                                {t('Close')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Portal>
    );
};

export default BalanceControlDialog;
